import React, { useEffect, useState } from "react";
import { Autocomplete, Box, Dialog, MenuItem, DialogActions, DialogContent, DialogTitle, Switch, TextField, Typography } from "@mui/material";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { format, isValid, startOfDay } from "date-fns";
import HeightButton from "../../common/height-button";
import typography from "../../cssUtils/typography";
import TimeSlotDropdown from "../BroadcastTemplates/CampaignOptions/SelectTimeSlot";
import { useAuth } from "../../hooks/AuthProvider";
import { FALLBACK_TEMPLATES_ENDPOINT } from "../../config/urls";
import RCSTemplatePreview from "../../common/RCSTemplatePreview";
import { ChannelBadge } from "./campaignBadges";
import TemplateMessagePreview from "./TemplateMessagePreview";

const cardSx = { bgcolor: "#fff", border: "1px solid #E3E5EA", borderRadius: "12px", overflow: "hidden" };
const cardTitleSx = { ...typography.bodyLarge, fontWeight: 700, px: 2.5, pt: 2, pb: 1.25 };

const Card = ({ title, children, footer, sx }) => (
  <Box sx={{ ...cardSx, display: "flex", flexDirection: "column", ...sx }}>
    {title && <Typography sx={cardTitleSx}>{title}</Typography>}
    <Box sx={{ px: 2.5, pb: 2.5, flex: 1 }}>{children}</Box>
    {footer}
  </Box>
);

// Bottom summary strip of the Audience / Schedule cards
const SummaryFoot = ({ label, value }) => (
  <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.25, px: 2.5, py: 1.5, borderTop: "1px solid #EEF0F3", bgcolor: "#FAFBFC" }}>
    <Typography sx={{ ...typography.bodySmall, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: "#9AA0AC" }}>
      {label}
    </Typography>
    <Typography sx={{ ...typography.bodySmall, fontWeight: value ? 600 : 400, color: value ? "#1C2333" : "#9AA0AC" }}>
      {value || "Not selected yet"}
    </Typography>
  </Box>
);

const Segmented = ({ options, value, onChange }) => (
  <Box sx={{ display: "inline-flex", alignSelf: "flex-start", flexWrap: "wrap", maxWidth: "100%", gap: 0.5, bgcolor: "#EDEFF3", borderRadius: "999px", p: 0.5 }}>
    {options.map((option) => (
      <Box
        key={option.value}
        component="button"
        type="button"
        onClick={() => onChange(option.value)}
        sx={{
          border: "none",
          cursor: "pointer",
          fontFamily: "inherit",
          ...typography.bodySmall,
          fontWeight: 600,
          px: 2,
          py: 1,
          borderRadius: "999px",
          bgcolor: value === option.value ? "#14204D" : "transparent",
          color: value === option.value ? "#fff" : "#5C6472",
        }}
      >
        {option.label}
      </Box>
    ))}
  </Box>
);

// "CSV Upload | Segment" toggle with the matching input below it
export const AudienceTypePicker = ({ value, onChange, csv, segment, disabled, note }) => (
  <Box>
    <Typography sx={{ ...typography.bodySmall, fontWeight: 600, color: "#3B4250", mb: 0.75 }}>Audience type *</Typography>
    <Segmented
      options={[
        { label: "CSV Upload", value: "csv" },
        { label: "Segment", value: "segment" },
      ]}
      value={value}
      onChange={(next) => !disabled && onChange(next)}
    />
    {note && <Typography sx={{ ...typography.bodySmall, color: "#6B7280", mt: 1 }}>{note}</Typography>}
    {value === "csv" && csv}
    {value === "segment" && segment}
  </Box>
);

const MetaRow =({ label, children }) => (
  <Box sx={{ display: "grid", gridTemplateColumns: "96px 1fr", columnGap: 2, alignItems: "center", justifyItems: "start", ...typography.bodySmall, color: "#6B7280" }}>
    <span>{label}</span>
    {children}
  </Box>
);

/**
 * Template variables as rows: {{n}} | label + source | locked value or input.
 * rows: [{ key, label, fixed (value shown locked) | inputProps (for the TextField) }]
 */
const VARIABLE_MAX_LENGTH = 60; // max characters for a template variable value

export const VariablesPanel = ({ rows }) => {
  const fixedCount = rows.filter((row) => row.fixed).length;
  return (
    <Box sx={{ border: "1px solid #E3E5EA", borderRadius: "10px", overflow: "hidden", mb: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.75, py: 1.25, bgcolor: "#F6F7F9", borderBottom: "1px solid #E3E5EA" }}>
        <Typography sx={{ ...typography.bodySmall, fontWeight: 700 }}>Variables</Typography>
        <Box sx={{ fontSize: 11, fontWeight: 700, bgcolor: "#14204D", color: "#fff", borderRadius: "999px", px: 1 }}>{rows.length}</Box>
        <Typography sx={{ ...typography.bodySmall, color: "#6B7280", ml: "auto" }}>
          {fixedCount} fixed · {rows.length - fixedCount} custom
        </Typography>
      </Box>
      {rows.map((row, i) => (
        <Box
          key={row.key}
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "54px 1fr", sm: "54px 1fr 1.1fr" },
            gap: 1.25,
            alignItems: "center",
            px: 1.75,
            py: 1.25,
            borderTop: i ? "1px solid #EEF0F3" : "none",
          }}
        >
          <Box sx={{ fontFamily: "ui-monospace, Menlo, Consolas, monospace", fontSize: 12, color: "#1D4ED8", bgcolor: "#E7EEFB", borderRadius: "6px", px: 0.75, py: 0.4, textAlign: "center" }}>
            {`{{${row.key}}}`}
          </Box>
          <Box>
            <Typography sx={{ ...typography.bodySmall, fontWeight: 600 }}>{row.label}</Typography>
            <Typography sx={{ fontSize: 11, color: "#9AA0AC", mt: 0.25 }}>
              {row.fixed ? `Mapped to ${row.fixed} · set in the template` : "Custom · enter for this broadcast"}
            </Typography>
          </Box>
          <Box sx={{ gridColumn: { xs: "1 / -1", sm: "auto" }, minWidth: 0 }}>
            {row.fixed ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, bgcolor: "#F6F7F9", border: "1px solid #E3E5EA", borderRadius: "8px", px: 1.25, py: 1, ...typography.bodySmall, color: "#5C6472" }}>
                <LockOutlinedIcon sx={{ fontSize: 14 }} />
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.fixed}</span>
              </Box>
            ) : (
              <TextField size="small" fullWidth required inputProps={{ maxLength: VARIABLE_MAX_LENGTH }} {...row.inputProps} />
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

// Pickers hold an Invalid Date while the user is still typing; treat it as "not set" instead of crashing format()
const isValidValue = (value) => !!value && isValid(new Date(value));

const HOURS = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, "0"));
const MINUTES = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, "0"));

// Date of draftDate with the dropdowns' 12-hour time applied
const combineTime = (date, hour, minute, period) => {
  if (!isValidValue(date)) return null;
  const when = new Date(date);
  when.setHours((Number(hour) % 12) + (period === "PM" ? 12 : 0), Number(minute), 0, 0);
  return when;
};

const scheduleText = (date, time) =>
  isValidValue(date) && isValidValue(time)
    ? `${format(new Date(date), "EEE, d MMM yyyy")} at ${format(new Date(time), "hh:mm a")}`
    : null;

/**
 * Single-page body of the WhatsApp / RCS broadcast forms for Campaigns (Beta):
 * Template, Audience, Schedule and Fallback cards plus the sticky footer.
 * All state and submit logic stay in the channel's form; this only lays them out.
 */
// ---------- Fallback to another channel (one leg; a broadcast has retry OR fallback) ----------
const CHANNEL_LABELS = { whatsapp: "WhatsApp", rcs: "RCS" };
const otherChannel = (channel) => (channel === "whatsapp" ? "rcs" : "whatsapp");
const FALLBACK_MIN_HOURS = 1;
const FALLBACK_MAX_HOURS = 72;

// customKeys: the chosen template's custom variables, values: what the user typed for them
export const EMPTY_FALLBACK = { enabled: false, botId: "", templateId: "", delayHours: "6", customKeys: [], values: {} };

// User-facing problem with the fallback setup, or null when it can be sent
export const fallbackError = (fallback) => {
  if (!fallback?.enabled) return null;
  if (!fallback.templateId) return "Select a template for the fallback";
  const hours = Number(fallback.delayHours);
  if (!Number.isInteger(hours) || hours < FALLBACK_MIN_HOURS || hours > FALLBACK_MAX_HOURS) {
    return `Fallback delay must be between ${FALLBACK_MIN_HOURS} and ${FALLBACK_MAX_HOURS} hours`;
  }
  if (fallback.customKeys.some((key) => !String(fallback.values[key] || "").trim())) {
    return "Fill in every custom variable of the fallback template";
  }
  return null;
};

// fallback_* request fields for a broadcast on `channel` (empty when fallback is off)
export const fallbackFields = (fallback, channel) =>
  fallback?.enabled
    ? {
        fallback_channel: otherChannel(channel),
        fallback_template_id: String(fallback.templateId),
        fallback_delay_hours: String(fallback.delayHours),
        fallback_payload: JSON.stringify(fallback.values),
      }
    : {};

// {{n}} shown for a variable key: bparameter_0 -> 1, hparameter -> H1, urlparameter_0 -> URL1
const variableTag = (key) => {
  if (key === "hparameter") return "H1";
  const [kind, index] = key.split("_");
  return kind === "urlparameter" ? `URL${Number(index) + 1}` : Number(index) + 1;
};

const FallbackStepBox = ({ title, note, children }) => (
  <Box sx={{ border: "1px solid #E3E5EA", borderRadius: "12px", p: 1.75, bgcolor: "#FAFBFC" }}>
    <Typography sx={{ ...typography.bodyLarge, fontWeight: 700 }}>
      {title}{" "}
      <Typography component="span" sx={{ ...typography.bodySmall, color: "#9AA0AC" }}>{note}</Typography>
    </Typography>
    {children}
  </Box>
);

const FallbackCard = ({ channel, showRetry, canFallback, enableTimeSlot, setEnableTimeSlot, timeSlot, setTimeSlot, fallback, setFallback }) => {
  const { getValidToken } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const target = otherChannel(channel);
  const fallbackOn = !!fallback?.enabled;
  const retryOn = !!showRetry && !!enableTimeSlot;
  const update = (changes) => setFallback((prev) => ({ ...prev, ...changes }));

  useEffect(() => {
    if (!fallbackOn || templates.length) return;
    const fetchTemplates = async () => {
      try {
        const response = await fetch(`${FALLBACK_TEMPLATES_ENDPOINT}?channel=${target}`, {
          headers: { Authorization: `Bearer ${await getValidToken()}`, "X-Tenant-Id": localStorage.getItem("tenant") },
        });
        const data = await response.json();
        setTemplates(data.results || []);
      } catch (error) {
        console.error(error);
      }
    };
    fetchTemplates();
  }, [fallbackOn, target]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (on) => {
    if (on && showRetry) setEnableTimeSlot(true); // retry is the default choice where it's available
    else if (on) update({ enabled: true });
    else {
      if (showRetry) setEnableTimeSlot(false);
      update({ enabled: false });
    }
  };
  const chooseMode = (mode) => {
    setEnableTimeSlot(mode === "retry");
    update({ enabled: mode === "fallback" });
  };
  // RCS templates belong to a bot: pick the bot first, then one of its templates
  const bots = target === "rcs"
    ? [...new Map(templates.filter((t) => t.bot_id).map((t) => [t.bot_id, { id: t.bot_id, name: t.bot_name }])).values()]
    : [];
  const selectedBot = bots.find((bot) => bot.id === fallback.botId) || null;
  const templateOptions = target === "rcs" ? templates.filter((t) => selectedBot && t.bot_id === selectedBot.id) : templates;
  const selectedTemplate = templates.find((t) => t.id === fallback.templateId) || null;
  const selectTemplate = (template) =>
    update({
      templateId: template ? template.id : "",
      customKeys: template ? template.variables.filter((v) => !v.mapped_to).map((v) => v.key) : [],
      values: {},
    });

  return (
    <Card title="Fallback options">
      <Typography sx={{ ...typography.bodySmall, color: "#6B7280", mt: -0.75, mb: 1.5 }}>
        Off by default. When on, messages that fail get a second attempt.
      </Typography>
      <Box sx={{ border: "1px solid #E3E5EA", borderRadius: "12px", p: 2 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}>
          <Box>
            <Typography sx={{ ...typography.bodyLarge, fontWeight: 700 }}>{showRetry ? "Enable Fallback & Retry" : "Enable Fallback"}</Typography>
            <Typography sx={{ ...typography.bodySmall, color: "#6B7280" }}>
              {showRetry && !canFallback
                ? `Retry on ${CHANNEL_LABELS[channel]} the messages that failed due to an ecosystem error.`
                : showRetry
                ? `Retry on ${CHANNEL_LABELS[channel]} or fall back to ${CHANNEL_LABELS[target]}, one of the two.`
                : `Send the failed messages again on ${CHANNEL_LABELS[target]}.`}
            </Typography>
          </Box>
          <Switch checked={retryOn || fallbackOn} onChange={(e) => toggle(e.target.checked)} />
        </Box>
        {(retryOn || fallbackOn) && (
          <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1.25 }}>
            <Box sx={{ border: "1px solid #E3E5EA", borderRadius: "10px", px: 1.5, py: 1.25, bgcolor: "#F6F7F9", ...typography.bodySmall, color: "#6B7280" }}>
              1 · {CHANNEL_LABELS[channel]} (this broadcast)
            </Box>
            {showRetry && canFallback && (
              <Segmented
                options={[
                  { label: "Retry on same channel", value: "retry" },
                  { label: `Fallback to ${CHANNEL_LABELS[target]}`, value: "fallback" },
                ]}
                value={fallbackOn ? "fallback" : "retry"}
                onChange={chooseMode}
              />
            )}
            {retryOn && (
              <FallbackStepBox title="2 · Retry on same channel" note="ecosystem errors only">
                <Box sx={{ maxWidth: 240, mt: 1.5 }}>
                  <TimeSlotDropdown value={timeSlot} onChange={setTimeSlot} />
                </Box>
                <Typography sx={{ ...typography.bodySmall, color: "#6B7280", mt: 1 }}>
                  Retries on {CHANNEL_LABELS[channel]} only the messages that failed due to an ecosystem error, {timeSlot} hours after this broadcast.
                </Typography>
              </FallbackStepBox>
            )}
            {fallbackOn && (
              <FallbackStepBox title={`2 · Fallback to ${CHANNEL_LABELS[target]}`} note="all failed messages">
                <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, mt: 1.5, ...typography.bodySmall, color: "#3B4250" }}>
                  ↳ Starts
                  <TextField
                    size="small"
                    type="number"
                    value={fallback.delayHours}
                    onChange={(e) => update({ delayHours: e.target.value })}
                    inputProps={{ min: FALLBACK_MIN_HOURS, max: FALLBACK_MAX_HOURS, style: { width: 56, textAlign: "center" } }}
                  />
                  h after this broadcast completes, for everyone whose message failed.
                </Box>
                {target === "rcs" && (
                  <Autocomplete
                    sx={{ mt: 1.5 }}
                    options={bots}
                    value={selectedBot}
                    onChange={(event, value) => update({ botId: value ? value.id : "", templateId: "", customKeys: [], values: {} })}
                    getOptionLabel={(option) => option.name || ""}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={(params) => <TextField {...params} label="RCS bot *" />}
                  />
                )}
                <Autocomplete
                  sx={{ mt: 1.5 }}
                  options={templateOptions}
                  disabled={target === "rcs" && !selectedBot}
                  value={selectedTemplate}
                  onChange={(event, value) => selectTemplate(value)}
                  getOptionLabel={(option) => option.label || ""}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => <TextField {...params} label={`${CHANNEL_LABELS[target]} template *`} />}
                />
                <Typography sx={{ ...typography.bodySmall, color: "#9AA0AC", mt: 0.75 }}>
                  {target === "whatsapp"
                    ? "Only approved templates that can be sent as they are (no location, media from the template itself) are listed."
                    : selectedBot
                    ? `Approved templates of ${selectedBot.name}.`
                    : "Select a bot to see its approved templates."}
                </Typography>
                {selectedTemplate?.variables?.length > 0 && (
                  <Box sx={{ mt: 1.5 }}>
                    <VariablesPanel
                      rows={selectedTemplate.variables.map((variable) =>
                        variable.mapped_to
                          ? { key: variableTag(variable.key), label: variable.label, fixed: variable.mapped_to }
                          : {
                              key: variableTag(variable.key),
                              label: variable.label,
                              inputProps: {
                                placeholder: "Enter value",
                                value: fallback.values[variable.key] || "",
                                onChange: (e) => update({ values: { ...fallback.values, [variable.key]: e.target.value } }),
                              },
                            },
                      )}
                    />
                  </Box>
                )}
                {selectedTemplate?.preview && (
                  <Box sx={{ mt: 1.5 }}>
                    <Box
                      component="button"
                      type="button"
                      onClick={() => setShowPreview((v) => !v)}
                      sx={{ border: "1px solid #D8DAE1", borderRadius: "8px", bgcolor: "#fff", px: 1.5, py: 0.75, cursor: "pointer", fontFamily: "inherit", ...typography.bodySmall, fontWeight: 600, color: "#14204D" }}
                    >
                      {showPreview ? "Hide preview" : "Show preview"}
                    </Box>
                    {showPreview && (
                      <Box sx={{ mt: 1.5, maxWidth: 380 }}>
                        {target === "rcs" ? (
                          <RCSTemplatePreview template={selectedTemplate.preview.template} bot={selectedTemplate.preview.bot} />
                        ) : (
                          <TemplateMessagePreview templatePreviewData={selectedTemplate.preview} />
                        )}
                      </Box>
                    )}
                  </Box>
                )}
              </FallbackStepBox>
            )}
          </Box>
        )}
      </Box>
    </Card>
  );
};

const BroadcastSections = ({
  channel,
  category, // optional { value, onChange } for WhatsApp's Marketing / Utility lists
  templateOptions,
  getTemplateLabel,
  selectedTemplate, // the selected template object (or null)
  onTemplateSelect,
  templateMeta = [], // [{ label, value }] rows shown under the dropdown
  templateNote,
  templateFormRef,
  templateFields,
  preview,
  audience,
  audienceTitle = "Audience",
  audienceSummary,
  hideSchedule = false, // quick broadcasts always go out now
  scheduleOnly = false, // editing a scheduled broadcast: it stays scheduled, only the time can change
  isLater,
  onSendNow,
  onSendLater,
  scheduleDate,
  scheduleTime,
  onScheduleApply,
  showRetry,
  fallback, // { enabled, templateId, delayHours, customKeys, values }; pass with setFallback to offer fallback
  setFallback,
  enableTimeSlot,
  setEnableTimeSlot,
  timeSlot,
  setTimeSlot,
  submitLabel,
  onCancel,
  onSubmit,
  loading,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [draftDate, setDraftDate] = useState(null);
  const [draftHour, setDraftHour] = useState("09");
  const [draftMinute, setDraftMinute] = useState("00");
  const [draftPeriod, setDraftPeriod] = useState("AM");
  const selected = selectedTemplate || null;

  const openScheduleDialog = () => {
    setDraftDate(isValidValue(scheduleDate) ? new Date(scheduleDate) : null);
    const time = isValidValue(scheduleTime) ? new Date(scheduleTime) : null;
    setDraftHour(time ? String(time.getHours() % 12 || 12).padStart(2, "0") : "09");
    setDraftMinute(time ? String(Math.floor(time.getMinutes() / 5) * 5).padStart(2, "0") : "00");
    setDraftPeriod(time && time.getHours() >= 12 ? "PM" : "AM");
    setDialogOpen(true);
  };

  const applySchedule = () => {
    onScheduleApply(draftDate, draftWhen);
    setDialogOpen(false);
  };

  const setSendTime = (value) => {
    if (value === "now") {
      onSendNow();
    } else {
      onSendLater();
      if (!scheduleDate || !scheduleTime) openScheduleDialog();
    }
  };

  const scheduledFor = scheduleText(scheduleDate, scheduleTime);
  const draftWhen = combineTime(draftDate, draftHour, draftMinute, draftPeriod);
  const draftInPast = !!draftWhen && draftWhen <= new Date();

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {/* Template */}
      <Card title="Template">
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) auto" }, gap: 3, alignItems: "start" }}>
          <Box>
            {category && (
              <Box sx={{ mb: 1.5 }}>
                <Segmented
                  options={[
                    { label: "Marketing", value: "marketing" },
                    { label: "Utility", value: "utility" },
                  ]}
                  value={category.value}
                  onChange={category.onChange}
                />
              </Box>
            )}
            <Autocomplete
              options={templateOptions}
              value={selected}
              onChange={(event, value) => onTemplateSelect(value)}
              getOptionLabel={(option) => getTemplateLabel(option) || ""}
              isOptionEqualToValue={(option, value) => getTemplateLabel(option) === getTemplateLabel(value)}
              renderInput={(params) => <TextField {...params} label="Select template *" />}
            />
            {selected && (
              <Box sx={{ mt: 1.75, display: "flex", flexDirection: "column", gap: 1, border: "1px solid #E3E5EA", borderRadius: "10px", px: 1.75, py: 1.5 }}>
                <MetaRow label="Channel">
                  <ChannelBadge channel={channel} />
                </MetaRow>
                {templateMeta.map((row) => (
                  <MetaRow key={row.label} label={row.label}>
                    <b style={{ color: "#1C2333" }}>{row.value || "—"}</b>
                  </MetaRow>
                ))}
                <MetaRow label="Status">
                  <b style={{ color: "#1E7B34" }}>Approved</b>
                </MetaRow>
              </Box>
            )}
            {/* Variables, header media and carousel inputs of the selected template (checked with the browser's form validation) */}
            <Box component="form" ref={templateFormRef} noValidate={false} onSubmit={(e) => e.preventDefault()} sx={{ mt: 1.75 }}>
              {selected && templateFields}
            </Box>
            <Typography sx={{ ...typography.bodySmall, color: "#9AA0AC", mt: 1.25 }}>{templateNote}</Typography>
          </Box>
          <Box>
            <Typography sx={{ ...typography.bodySmall, color: "#9AA0AC", mb: 0.5 }}>Template preview</Typography>
            {selected ? preview : <Typography sx={{ ...typography.bodySmall, color: "#9AA0AC" }}>Select a template to see its preview.</Typography>}
          </Box>
        </Box>
      </Card>

      {/* Audience, Schedule and Fallback stay disabled until a template is selected */}
      {!selected && (
        <Typography sx={{ ...typography.bodySmall, color: "#6B7280", mb: -1 }}>
          Select a template above to set the audience and schedule.
        </Typography>
      )}
      <Box
        aria-disabled={!selected}
        {...(!selected && { inert: "" })}
        sx={{ display: "flex", flexDirection: "column", gap: 2, ...(!selected && { opacity: 0.5, pointerEvents: "none", userSelect: "none" }) }}
      >
      {/* Audience + Schedule */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: hideSchedule ? "1fr" : "1fr 1fr" }, gap: 2, alignItems: "stretch" }}>
        <Card title={audienceTitle} footer={<SummaryFoot label={audienceTitle} value={audienceSummary} />}>
          {audience}
        </Card>
        {!hideSchedule && (
        <Card title="Schedule" footer={<SummaryFoot label="Send time" value={isLater ? scheduledFor : "Immediately"} />}>
          <Typography sx={{ ...typography.bodySmall, fontWeight: 600, color: "#3B4250", mb: 0.75 }}>Send time *</Typography>
          {!scheduleOnly && <Segmented
            options={[
              { label: "Send now", value: "now" },
              { label: "Schedule for later", value: "later" },
            ]}
            value={isLater ? "later" : "now"}
            onChange={setSendTime}
          />}
          {isLater ? (
            <Box
              component="button"
              type="button"
              onClick={openScheduleDialog}
              sx={{ mt: 1.5, display: "flex", alignItems: "center", gap: 1.25, width: "100%", border: "1px solid #D8DAE1", borderRadius: "10px", px: 1.75, py: 1.4, bgcolor: "#fff", cursor: "pointer", fontFamily: "inherit", ...typography.bodyLarge, textAlign: "left" }}
            >
              <CalendarTodayOutlinedIcon sx={{ fontSize: 18, color: "#14204D" }} />
              {scheduledFor ? "Change date & time" : "Select date & time"}
            </Box>
          ) : (
            <Typography sx={{ ...typography.bodySmall, color: "#6B7280", mt: 1.5 }}>The broadcast goes out as soon as you confirm.</Typography>
          )}
        </Card>
        )}
      </Box>

      {/* Fallback options: retry on the same channel or fallback to the other one */}
      {(showRetry || fallback) && (
        <FallbackCard
          channel={channel}
          showRetry={showRetry}
          canFallback={!!fallback}
          enableTimeSlot={enableTimeSlot}
          setEnableTimeSlot={setEnableTimeSlot}
          timeSlot={timeSlot}
          setTimeSlot={setTimeSlot}
          fallback={fallback || EMPTY_FALLBACK}
          setFallback={setFallback || (() => {})}
        />
      )}
      </Box>

      {/* Sticky footer */}
      <Box sx={{ position: "sticky", bottom: 0, zIndex: 10, bgcolor: "#FAFAFA", borderTop: "1px solid #E3E5EA", py: 1.75, display: "flex", justifyContent: "flex-end", gap: 1.25 }}>
        <HeightButton variant="outlined" title="Cancel" onClick={onCancel} />
        <HeightButton variant="contained" title={loading ? "Please wait..." : submitLabel} onClick={onSubmit} disabled={loading} />
      </Box>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ ...typography.cardTitle, fontWeight: 700 }}>Schedule broadcast</DialogTitle>
        <DialogContent sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "300px 1fr" }, gap: 3.5, pt: "8px !important" }}>
          <Box>
            <Typography sx={{ ...typography.bodySmall, fontWeight: 600, color: "#3B4250", mb: 0.75 }}>Date</Typography>
            <Box sx={{ border: "1px solid #E3E5EA", borderRadius: "12px" }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateCalendar
                  value={draftDate}
                  onChange={(value) => setDraftDate(value ? startOfDay(value) : null)}
                  disablePast
                  sx={{
                    width: "100%",
                    "& .MuiPickersDay-root.Mui-selected, & .MuiPickersDay-root.Mui-selected:hover, & .MuiPickersDay-root.Mui-selected:focus": { bgcolor: "#14204D" },
                    "& .MuiPickersDay-root": { borderRadius: "8px" },
                  }}
                />
              </LocalizationProvider>
            </Box>
          </Box>
          <Box>
            <Typography sx={{ ...typography.bodySmall, fontWeight: 600, color: "#3B4250", mb: 0.75 }}>Time</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TextField select size="small" value={draftHour} onChange={(e) => setDraftHour(e.target.value)} sx={{ flex: 1, minWidth: 84 }}>
                {HOURS.map((h) => <MenuItem key={h} value={h}>{h}</MenuItem>)}
              </TextField>
              <Typography sx={{ fontWeight: 700 }}>:</Typography>
              <TextField select size="small" value={draftMinute} onChange={(e) => setDraftMinute(e.target.value)} sx={{ flex: 1, minWidth: 84 }}>
                {MINUTES.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
              </TextField>
              <TextField select size="small" value={draftPeriod} onChange={(e) => setDraftPeriod(e.target.value)} sx={{ flex: 1, minWidth: 84 }}>
                {["AM", "PM"].map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
              </TextField>
            </Box>
            <Typography sx={{ ...typography.bodySmall, color: draftInPast ? "#C0554B" : "#3B4250", bgcolor: "#F6F7F9", borderRadius: "10px", px: 1.5, py: 1.25, mt: 2 }}>
              {!draftWhen ? "Pick a date to schedule this broadcast." : draftInPast ? "That time has already passed. Pick a later time." : <>Scheduled for <b>{scheduleText(draftDate, draftWhen)}</b></>}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <HeightButton variant="outlined" title="Cancel" onClick={() => setDialogOpen(false)} />
          <HeightButton variant="contained" title="Apply" onClick={applySchedule} disabled={!draftWhen || draftInPast} />
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BroadcastSections;
